import {
  ArrowLeft,
  Ban,
  CalendarDays,
  CircleCheck,
  ClipboardCheck,
  Pencil,
  Pause,
  Play,
  RefreshCw,
  Table2,
  Target,
  Trash2,
  TrendingDown,
  TrendingUp,
  Trophy,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SectionCard } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { BarChart } from '@/components/charts/bar-chart';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { useAuthStore, selectUser } from '@/features/auth/auth.store';
import { formatMetricValue } from '@/lib/metric-format';
import { cn, formatDateValue, formatDateTimeValue, formatHoraValue, toNumber } from '@/lib/utils';

import { MetaProgress } from '../components/meta-progress';
import { MetasNav } from '../components/metas-nav';
import {
  useMeta,
  useMetaAuditoria,
  useMetaComparativo,
  useMetaConsolidado,
  useMetaEvaluaciones,
} from '../hooks/use-metas';
import {
  useCambiarEstadoMeta,
  useEliminarMeta,
  useMarcarCumplida,
  useRecalcularMeta,
} from '../hooks/use-metas-mutations';
import {
  avance,
  DESEMPENO_LABELS,
  DESEMPENO_TONES,
  diasRestantes,
  diasRestantesDe,
  ESTADO_TONES,
  estadoLabel,
  formatoDeUnidad,
  PRIORIDAD_TONES,
  prioridadLabel,
  responsable,
  TONO_A_BADGE,
  tipoLabel,
  valorConUnidad,
} from '../lib/metas-format';
import type { DailyConsolidatedRow, GoalStatus } from '../metas.types';

/** Porcentaje con dos decimales; `—` cuando el backend no envía el dato. */
function pct(valor: unknown): string {
  return valor === null || valor === undefined ? '—' : `${toNumber(valor).toFixed(2)} %`;
}

/** Día (fecha + etiqueta del backend) con barras y totales. */
const COLUMNAS_CONSOLIDADO =
  'grid grid-cols-[minmax(0,7rem)_minmax(0,1fr)_6.5rem_6.5rem_6.5rem_5.5rem_7.5rem] items-center gap-x-3';

const TONO_TEXTO_VARIACION: Record<string, string> = {
  emerald: 'text-emerald-600',
  rose: 'text-rose-600',
  ink: 'text-ink-400',
};

/** Tarjeta de indicador del detalle. */
function Dato({
  label,
  valor,
  detalle,
  className,
  valorClassName,
}: {
  label: string;
  valor: string;
  detalle?: string;
  className?: string;
  valorClassName?: string;
}) {
  return (
    <div className={cn('rounded-card border border-ink-100 bg-white px-4 py-3', className)}>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">{label}</p>
      <p
        className={cn(
          'mt-1 font-mono text-lg font-bold tabular-nums text-ink-900',
          valorClassName,
        )}
      >
        {valor}
      </p>
      {detalle ? <p className="mt-0.5 text-xs text-ink-500">{detalle}</p> : null}
    </div>
  );
}

/** Fila del consolidado diario. */
function FilaConsolidado({
  fila,
  formato,
}: {
  fila: DailyConsolidatedRow;
  formato: ReturnType<typeof formatoDeUnidad>;
}) {
  const cumplimiento = toNumber(fila.pct_vs_expected_day);
  const tieneCumplimiento = fila.pct_vs_expected_day !== null && fila.pct_vs_expected_day !== undefined;
  const anterior = fila.same_day_previous_month_value;
  /** El backend sólo envía la diferencia cuando tiene ambos meses; si no, se calcula. */
  const variacion =
    anterior === null || anterior === undefined
      ? null
      : fila.difference_vs_previous === null || fila.difference_vs_previous === undefined
        ? fila.value - toNumber(anterior)
        : toNumber(fila.difference_vs_previous);

  return (
    <li className={cn(COLUMNAS_CONSOLIDADO, 'border-b border-ink-100 py-2 last:border-b-0')}>
      <span className="flex min-w-0 flex-col">
        <span className="truncate text-sm font-semibold text-ink-800">
          {formatDateValue(fila.date_key)}
        </span>
        <span className="text-[11px] text-ink-400">{fila.label}</span>
      </span>

      <span className="truncate font-mono text-xs text-ink-500 tabular-nums">
        {formatHoraValue(fila.window_start)} → {formatHoraValue(fila.window_end)}
      </span>

      <span className="truncate text-right font-mono text-xs text-ink-800 tabular-nums">
        {formatMetricValue(fila.value, formato)}
      </span>

      <span className="truncate text-right font-mono text-xs font-semibold text-ink-900 tabular-nums">
        {formatMetricValue(fila.running_total, formato)}
      </span>

      <span className="truncate text-right font-mono text-xs text-ink-500 tabular-nums">
        {formatMetricValue(fila.expected_cumulative, formato)}
      </span>

      <span className="flex justify-end">
        {tieneCumplimiento ? (
          <Badge variant={cumplimiento >= 100 ? 'success' : cumplimiento >= 80 ? 'warning' : 'danger'}>
            {cumplimiento.toFixed(1)}%
          </Badge>
        ) : (
          <span className="text-xs text-ink-300">—</span>
        )}
      </span>

      <span className="flex min-w-0 flex-col items-end">
        <span className="w-full truncate text-right font-mono text-xs text-ink-600 tabular-nums">
          {anterior === null || anterior === undefined
            ? '—'
            : formatMetricValue(anterior, formato)}
        </span>
        {variacion !== null && Number.isFinite(variacion) && variacion !== 0 ? (
          <span
            className={cn(
              'flex items-center gap-0.5 text-[11px] font-semibold tabular-nums',
              variacion >= 0 ? 'text-emerald-600' : 'text-rose-600',
            )}
          >
            {variacion >= 0 ? (
              <TrendingUp className="size-3" aria-hidden="true" />
            ) : (
              <TrendingDown className="size-3" aria-hidden="true" />
            )}
            {formatMetricValue(Math.abs(variacion), formato)}
          </span>
        ) : null}
      </span>
    </li>
  );
}

/**
 * Detalle de una meta (`/metas/:id`).
 *
 * Reúne el avance, el comparativo contra el período anterior, el consolidado
 * diario (ventanas de turno), las evaluaciones y la auditoría, más las acciones
 * de gestión (editar, evaluar, recalcular, cambiar estado y eliminar).
 */
export function MetaDetallePage() {
  const { id: idParam } = useParams();
  const id = Number(idParam);
  const navigate = useNavigate();

  const usuario = useAuthStore(selectUser);

  const [aviso, setAviso] = useState<{ tipo: 'success' | 'error'; texto: string } | null>(null);

  const meta = useMeta(Number.isFinite(id) ? id : null);
  const comparativo = useMetaComparativo(Number.isFinite(id) ? id : null);
  const consolidado = useMetaConsolidado(Number.isFinite(id) ? id : null);
  const evaluaciones = useMetaEvaluaciones(Number.isFinite(id) ? id : null);
  const auditoria = useMetaAuditoria(Number.isFinite(id) ? id : null);

  const recalcular = useRecalcularMeta();
  const cambiarEstado = useCambiarEstadoMeta();
  const marcarCumplida = useMarcarCumplida();
  const eliminar = useEliminarMeta();

  const goal = meta.data;

  useDocumentTitle(goal ? `Meta · ${goal.goal_name}` : 'Meta');

  const formato = formatoDeUnidad(goal?.unit_measurement);
  const filas = useMemo(() => consolidado.data?.data.rows ?? [], [consolidado.data]);
  const totales = consolidado.data?.data.totals;

  /** Cambia el estado y avisa el resultado. */
  const aplicarEstado = (status: GoalStatus, texto: string) => {
    if (!goal) return;

    cambiarEstado.mutate(
      { id: goal.id, status },
      {
        onSuccess: () => setAviso({ tipo: 'success', texto }),
        onError: (error) =>
          setAviso({ tipo: 'error', texto: error.message || 'No se pudo cambiar el estado.' }),
      },
    );
  };

  if (meta.isError) {
    return (
      <div className="space-y-6">
        <Alert variant="error" title="No se pudo cargar la meta">
          <p>La meta no existe o no tienes acceso a ella.</p>
          <Link to="/metas/listado" className="mt-2 inline-flex text-sm font-semibold text-brand-700">
            Volver al listado
          </Link>
        </Alert>
      </div>
    );
  }

  if (meta.isPending || !goal) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-24 rounded-card" />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-20 rounded-card" />
          ))}
        </div>
        <Skeleton className="h-80 rounded-card" />
      </div>
    );
  }

  const estado = goal.status;
  const desempeno = goal.performance_status;
  const unidades = goal.unit_measurement;
  const faltante = Math.max(toNumber(goal.target_value) - toNumber(goal.current_value), 0);

  return (
    <div className="space-y-6">
      <header className="space-y-4">
        <Link
          to="/metas/listado"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-ink-500 transition-colors hover:text-brand-700"
        >
          <ArrowLeft className="size-3.5" aria-hidden="true" />
          Volver al listado
        </Link>

        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Meta</p>
            <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
              <Target className="size-6 shrink-0 text-brand-500" aria-hidden="true" />
              {goal.goal_name}
            </h1>

            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Badge variant={TONO_A_BADGE[ESTADO_TONES[estado]]}>{estadoLabel(estado)}</Badge>
              <Badge variant={TONO_A_BADGE[PRIORIDAD_TONES[goal.priority]]}>
                Prioridad {prioridadLabel(goal.priority).toLowerCase()}
              </Badge>
              {desempeno ? (
                <Badge variant={TONO_A_BADGE[DESEMPENO_TONES[desempeno]]}>
                  {DESEMPENO_LABELS[desempeno]}
                </Badge>
              ) : null}
              <Badge variant="outline">{tipoLabel(goal.goal_type)}</Badge>
            </div>
          </div>

          <MetasNav active="listado" className="justify-end" />
        </div>
      </header>

      {aviso ? (
        <Alert variant={aviso.tipo} title={aviso.tipo === 'success' ? 'Listo' : 'Revisa el dato'}>
          {aviso.texto}
        </Alert>
      ) : null}

      {/* Indicadores */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Dato
          label="Avance"
          valor={pct(avance(goal))}
          detalle={desempeno ? DESEMPENO_LABELS[desempeno] : estadoLabel(estado)}
        />
        <Dato
          label="Acumulado"
          valor={formatMetricValue(goal.current_value, formato)}
          detalle={
            faltante > 0
              ? `Falta ${valorConUnidad(faltante, unidades)}`
              : 'Objetivo alcanzado'
          }
        />
        <Dato
          label="Objetivo"
          valor={formatMetricValue(goal.target_value, formato)}
          detalle={`Del ${formatDateValue(goal.start_date)} al ${formatDateValue(goal.end_date)}`}
        />
        <Dato
          label="Días restantes"
          valor={String(diasRestantesDe(goal))}
          detalle={
            estado === 'completed'
              ? 'Período finalizado'
              : estado === 'cancelled'
                ? 'Meta cancelada'
                : diasRestantes(diasRestantesDe(goal))
          }
        />
      </div>

      <SectionCard title="Avance general" description="Cumplimiento acumulado del período">
        <MetaProgress goal={goal} detallado />
      </SectionCard>

      {/* Comparativo + condiciones */}
      <div className="grid items-start gap-6 xl:grid-cols-3">
        <SectionCard
          className="xl:col-span-2"
          title="Comparativo por período"
          description="Acumulado actual frente al período anterior"
        >
          {comparativo.isPending ? (
            <Skeleton className="h-64 rounded-card" />
          ) : comparativo.data ? (
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-3">
                <Dato
                  label="Acumulado actual"
                  valor={formatMetricValue(comparativo.data.values[1] ?? 0, formato)}
                  detalle={`${formatDateValue(comparativo.data.current_period.start)} → ${formatDateValue(comparativo.data.current_period.end)}`}
                />
                <Dato
                  label="Período anterior"
                  valor={formatMetricValue(comparativo.data.values[0] ?? 0, formato)}
                  detalle={`${formatDateValue(comparativo.data.previous_period.start)} → ${formatDateValue(comparativo.data.previous_period.end)}`}
                />
                <Dato
                  label="Variación"
                  valor={pct(comparativo.data.comparison_percentage)}
                  detalle={
                    comparativo.data.comparison_percentage === null ||
                    comparativo.data.comparison_percentage === undefined
                      ? 'Sin período anterior comparable'
                      : comparativo.data.comparison_percentage >= 0
                        ? 'Por encima del período anterior'
                        : 'Por debajo del período anterior'
                  }
                  valorClassName={
                    comparativo.data.comparison_percentage === null ||
                    comparativo.data.comparison_percentage === undefined
                      ? undefined
                      : TONO_TEXTO_VARIACION[
                          comparativo.data.comparison_percentage >= 0 ? 'emerald' : 'rose'
                        ]
                  }
                />
              </div>

              <BarChart
                labels={comparativo.data.labels}
                height={260}
                format={formato}
                series={[
                  {
                    label: `Valor (${unidades ?? 'unidad'})`,
                    data: comparativo.data.values,
                  },
                ]}
              />
            </div>
          ) : (
            <p className="py-8 text-center text-sm text-ink-500">
              No hay información comparativa para esta meta.
            </p>
          )}
        </SectionCard>

        <SectionCard title="Condiciones de la meta" description="Cómo se calcula el avance">
          <dl className="space-y-3 text-sm">
            <div className="flex items-start justify-between gap-3">
              <dt className="text-ink-500">Responsable</dt>
              <dd className="text-right font-medium text-ink-800">{responsable(goal)}</dd>
            </div>
            <div className="flex items-start justify-between gap-3">
              <dt className="text-ink-500">Producto</dt>
              <dd className="text-right font-medium text-ink-800">
                {goal.fuel_grade_name || 'Todos los productos'}
              </dd>
            </div>
            <div className="flex items-start justify-between gap-3">
              <dt className="text-ink-500">Lado</dt>
              <dd className="text-right font-medium text-ink-800">
                {goal.pump_name || (goal.pump_id ? `Lado ${goal.pump_id}` : 'Todos los lados')}
              </dd>
            </div>
            <div className="flex items-start justify-between gap-3">
              <dt className="text-ink-500">Unidad</dt>
              <dd className="text-right font-medium text-ink-800">{unidades || 'Sin unidad'}</dd>
            </div>
            <div className="flex items-start justify-between gap-3">
              <dt className="text-ink-500">Período</dt>
              <dd className="flex items-center justify-end gap-1.5 text-right font-medium text-ink-800">
                <CalendarDays className="size-3.5 text-ink-400" aria-hidden="true" />
                {formatDateValue(goal.start_date)} → {formatDateValue(goal.end_date)}
              </dd>
            </div>
            <div className="flex items-start justify-between gap-3">
              <dt className="text-ink-500">Creada</dt>
              <dd className="text-right font-medium text-ink-800">
                {formatDateTimeValue(goal.created_at)}
              </dd>
            </div>
            <div className="flex items-start justify-between gap-3">
              <dt className="text-ink-500">Última actualización</dt>
              <dd className="text-right font-medium text-ink-800">
                {formatDateTimeValue(goal.updated_at)}
              </dd>
            </div>
          </dl>

          <div className="mt-4 flex flex-wrap gap-2 border-t border-ink-100 pt-4">
            <Badge variant={goal.include_credits ? 'success' : 'neutral'}>
              {goal.include_credits ? 'Incluye créditos' : 'Sin créditos'}
            </Badge>
            <Badge variant={goal.include_internal_consumption ? 'success' : 'neutral'}>
              {goal.include_internal_consumption ? 'Incluye consumo interno' : 'Sin consumo interno'}
            </Badge>
            <Badge variant={goal.include_calibrations ? 'success' : 'neutral'}>
              {goal.include_calibrations ? 'Incluye calibraciones' : 'Sin calibraciones'}
            </Badge>
          </div>

          {goal.notes ? (
            <div className="mt-4 border-t border-ink-100 pt-4">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">Notas</p>
              <p className="mt-1 text-sm leading-relaxed text-ink-700">{goal.notes}</p>
            </div>
          ) : null}

          {goal.reward_description ? (
            <div className="mt-4 border-t border-ink-100 pt-4">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                Recompensa
              </p>
              <p className="mt-1 text-sm leading-relaxed text-ink-700">{goal.reward_description}</p>
            </div>
          ) : null}
        </SectionCard>
      </div>

      {/* Consolidado diario */}
      <SectionCard
        title="Consolidado diario"
        description="Ventanas de turno desde la apertura hasta el cierre de cada día"
        action={
          totales ? (
            <div className="flex flex-wrap items-center gap-2 text-xs text-ink-500">
              <Badge variant="outline">{totales.days} días</Badge>
              <span className="font-mono tabular-nums">
                Total {formatMetricValue(totales.value, formato)}
              </span>
              <span className="font-mono tabular-nums">
                Esperado/día {formatMetricValue(totales.expected_daily_value, formato)}
              </span>
            </div>
          ) : undefined
        }
      >
        {consolidado.isPending ? (
          <Skeleton className="h-64 rounded-card" />
        ) : filas.length === 0 ? (
          <div className="rounded-control border border-dashed border-ink-200 px-4 py-10 text-center">
            <Table2 className="mx-auto size-5 text-ink-300" aria-hidden="true" />
            <p className="mt-2 text-sm font-medium text-ink-700">Sin consolidado diario</p>
            <p className="mt-0.5 text-xs text-ink-500">
              El período de la meta todavía no tiene días con operación registrada.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="min-w-[52rem]">
              <div
                className={cn(
                  COLUMNAS_CONSOLIDADO,
                  'border-b border-ink-100 pb-2 text-[11px] font-semibold uppercase tracking-wider text-ink-400',
                )}
              >
                <span>Día</span>
                <span>Ventana de turno</span>
                <span className="text-right">Día</span>
                <span className="text-right">Acumulado</span>
                <span className="text-right">Esperado</span>
                <span className="text-right">Cumplimiento</span>
                <span className="text-right">Mes anterior</span>
              </div>

              <ul className="mt-1">
                {filas.map((fila) => (
                  <FilaConsolidado key={fila.date_key} fila={fila} formato={formato} />
                ))}
              </ul>
            </div>
          </div>
        )}

        {totales && totales.excluded_total > 0 ? (
          <p className="mt-3 border-t border-ink-100 pt-3 text-xs text-ink-500">
            Excluidos del cálculo:{' '}
            <span className="font-mono tabular-nums">
              {formatMetricValue(totales.excluded_total, formato)}
            </span>{' '}
            (créditos {formatMetricValue(totales.excluded_credits, formato)}, calibraciones{' '}
            {formatMetricValue(totales.excluded_calibrations, formato)}, consumo interno{' '}
            {formatMetricValue(totales.excluded_internal_consumption, formato)}).
          </p>
        ) : null}
      </SectionCard>

      {/* Gestión + evaluaciones + auditoría */}
      <div className="grid items-start gap-6 xl:grid-cols-3">
        <SectionCard title="Gestión de la meta" description="Acciones disponibles según su estado">
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              leftIcon={<RefreshCw className="size-3.5" aria-hidden="true" />}
              loading={recalcular.isPending}
              onClick={() =>
                recalcular.mutate(goal.id, {
                  onSuccess: (progreso) =>
                    setAviso({
                      tipo: 'success',
                      texto: `Avance recalculado: ${formatMetricValue(progreso.current_value, formato)} de ${formatMetricValue(progreso.target_value, formato)} (${toNumber(progreso.progress_percentage).toFixed(2)} %).`,
                    }),
                  onError: (error) =>
                    setAviso({ tipo: 'error', texto: error.message || 'No se pudo recalcular.' }),
                })
              }
            >
              Recalcular avance
            </Button>

            <Link
              to={`/metas/${goal.id}/editar`}
              className="inline-flex h-9 items-center gap-2 rounded-control border border-ink-200 bg-white px-3.5 text-sm font-semibold text-ink-800 transition-colors hover:border-brand-300 hover:text-brand-700"
            >
              <Pencil className="size-3.5" aria-hidden="true" />
              Editar
            </Link>

            <Link
              to={`/metas/${goal.id}/evaluar`}
              className="inline-flex h-9 items-center gap-2 rounded-control border border-ink-200 bg-white px-3.5 text-sm font-semibold text-ink-800 transition-colors hover:border-brand-300 hover:text-brand-700"
            >
              <ClipboardCheck className="size-3.5" aria-hidden="true" />
              Evaluar
            </Link>
          </div>

          <div className="mt-5 space-y-2 border-t border-ink-100 pt-4">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
              Cambiar estado
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant={estado === 'active' ? 'secondary' : 'outline'}
                leftIcon={<Play className="size-3.5" aria-hidden="true" />}
                disabled={estado === 'active'}
                onClick={() => aplicarEstado('active', 'La meta volvió a estar activa.')}
              >
                Activar
              </Button>
              <Button
                size="sm"
                variant={estado === 'paused' ? 'secondary' : 'outline'}
                leftIcon={<Pause className="size-3.5" aria-hidden="true" />}
                disabled={estado === 'paused'}
                onClick={() => aplicarEstado('paused', 'La meta quedó en pausa.')}
              >
                Pausar
              </Button>
              <Button
                size="sm"
                variant={estado === 'cancelled' ? 'secondary' : 'outline'}
                leftIcon={<Ban className="size-3.5" aria-hidden="true" />}
                disabled={estado === 'cancelled'}
                onClick={() => aplicarEstado('cancelled', 'La meta quedó cancelada.')}
              >
                Cancelar
              </Button>
              <Button
                size="sm"
                variant={estado === 'completed' ? 'secondary' : 'outline'}
                leftIcon={<CircleCheck className="size-3.5" aria-hidden="true" />}
                disabled={estado === 'completed'}
                onClick={() =>
                  marcarCumplida.mutate(
                    { id: goal.id, userId: usuario?.id ?? null },
                    {
                      onSuccess: () =>
                        setAviso({ tipo: 'success', texto: 'La meta quedó marcada como cumplida.' }),
                      onError: (error) =>
                        setAviso({
                          tipo: 'error',
                          texto: error.message || 'No se pudo marcar como cumplida.',
                        }),
                    },
                  )
                }
              >
                Marcar cumplida
              </Button>
            </div>
          </div>

          <div className="mt-5 border-t border-ink-100 pt-4">
            <Button
              size="sm"
              variant="danger"
              leftIcon={<Trash2 className="size-3.5" aria-hidden="true" />}
              loading={eliminar.isPending}
              onClick={() => {
                if (!window.confirm(`¿Eliminar la meta "${goal.goal_name}"? Esta acción no se puede deshacer.`)) {
                  return;
                }

                eliminar.mutate(goal.id, {
                  onSuccess: () => {
                    navigate('/metas/listado', { replace: true });
                  },
                  onError: (error) =>
                    setAviso({ tipo: 'error', texto: error.message || 'No se pudo eliminar la meta.' }),
                });
              }}
            >
              Eliminar meta
            </Button>
          </div>
        </SectionCard>

        <SectionCard
          className="xl:col-span-2"
          title="Evaluaciones"
          description="Seguimiento registrado por los responsables"
          action={
            <Link
              to={`/metas/${goal.id}/evaluar`}
              className="inline-flex items-center gap-1.5 rounded-control bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-700"
            >
              <ClipboardCheck className="size-3.5" aria-hidden="true" />
              Nueva evaluación
            </Link>
          }
        >
          {evaluaciones.isPending ? (
            <Skeleton className="h-40 rounded-card" />
          ) : (evaluaciones.data ?? []).length === 0 ? (
            <div className="rounded-control border border-dashed border-ink-200 px-4 py-10 text-center">
              <ClipboardCheck className="mx-auto size-5 text-ink-300" aria-hidden="true" />
              <p className="mt-2 text-sm font-medium text-ink-700">Sin evaluaciones</p>
              <p className="mt-0.5 text-xs text-ink-500">
                Registra una evaluación para dejar constancia del avance y las acciones a tomar.
              </p>
            </div>
          ) : (
            <ul className="space-y-3">
              {(evaluaciones.data ?? []).map((evaluacion) => (
                <li key={evaluacion.id} className="rounded-control border border-ink-100 px-4 py-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-ink-900">
                        {formatDateValue(evaluacion.evaluation_date)}
                      </span>
                      <Badge variant={evaluacion.is_on_track ? 'success' : 'danger'}>
                        {evaluacion.is_on_track ? 'En camino' : 'En riesgo'}
                      </Badge>
                    </div>
                    <span className="font-mono text-xs text-ink-500 tabular-nums">
                      {formatMetricValue(evaluacion.current_value, formato)} ·{' '}
                      {pct(evaluacion.progress_percentage)}
                    </span>
                  </div>

                  {evaluacion.evaluation_notes ? (
                    <p className="mt-2 text-sm leading-relaxed text-ink-700">
                      {evaluacion.evaluation_notes}
                    </p>
                  ) : null}

                  <p className="mt-1.5 text-[11px] text-ink-400">
                    {[evaluacion.first_name, evaluacion.last_name].filter(Boolean).join(' ') ||
                      'Sistema'}{' '}
                    · {formatDateTimeValue(evaluacion.created_at)}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </SectionCard>
      </div>

      <SectionCard title="Auditoría" description="Cambios registrados sobre la meta">
        {auditoria.isPending ? (
          <Skeleton className="h-40 rounded-card" />
        ) : (auditoria.data ?? []).length === 0 ? (
          <p className="py-8 text-center text-sm text-ink-500">Sin movimientos registrados.</p>
        ) : (
          <ul className="divide-y divide-ink-100">
            {(auditoria.data ?? []).map((registro) => (
              <li key={registro.id} className="flex flex-wrap items-start justify-between gap-2 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-ink-800">
                    {registro.action_label || registro.action}
                  </p>
                  <p className="mt-0.5 text-xs leading-relaxed text-ink-500">
                    {registro.description}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-ink-500">
                    {[registro.first_name, registro.last_name].filter(Boolean).join(' ') || 'Sistema'}
                  </p>
                  <p className="text-[11px] text-ink-400">
                    {formatDateTimeValue(registro.created_at)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>

      {estado === 'completed' ? (
        <p className="flex items-center gap-2 text-xs text-ink-500">
          <Trophy className="size-3.5 text-amber-500" aria-hidden="true" />
          Meta cumplida: el avance se conserva como referencia histórica.
        </p>
      ) : null}
    </div>
  );
}
